import type { CSSProperties } from "react";

import { createTheme, type ThemeOptions } from "@mui/material/styles";

declare module "@mui/material/Chip" {
  interface ChipPropsVariantOverrides {
    roundedOutlined: true;
    roundedFilled: true;
  }
}

declare module "@mui/material/Divider" {
  interface DividerPropsVariantOverrides {
    dashedWithLabel: true;
  }
}

export const colors = {
  primary: "#EB5E2A",
  textSecondary: "hsla(145, 44.00%, 19.60%, 0.60)",
  secondary: "#1C482E",
  disabled: "#AFAFAF",
  link: "#026FFF",
  primaryLight: "rgba(14, 106, 64, 0.12)",
  primaryExtraLight: "rgba(0, 110, 255, 0.07)",
  background: "#F6EFE4",
  backgroundVariant: "#E5DDCF",
  backgroundTertiary: "#E9E3D7",
  backgroundQuaternary: "#FAF9F6",
  backgroundVariant2: "#FEF5F5",
  text: "#1C482E",
  textInverse: "#FFFFFF",
  textVariant: "rgba(10, 33, 31, 0.6)",
  border: "#E2E4E4",
  red: {
    main: "#C41230",
    light: "#F9E3E7",
    dark: "#E30C00",
    100: "#F05252",
    150: "#E02424"
  },
  delete: "#C81E1E",
  white: "#FFF",
  black: "#000",
  additionalGreen: "#117046",
  accentGreen: "#013E22",
  yellow: "#FACC15",
  ambassadorPurple: "#9F4CBD",
  grey: {
    main: "#C7C7C7",
    light: "#E9E9E9",
    dark: "#7D7D7D",
  },
  green: {
    light: "#CACEC0",
    100: "rgba(202, 206, 192, 1)",
  },
  beige: {
    100: "#E5DDCF",
  },
  greenLight: "#9FAC9B",
  pink: {
    dark: "#622B5B",
    main: "#D8B1D3",
  },
  brandPink: "#D8B1D3",
  brandBlue: "#BBD9D9",
  brandGreen: "#1C482E",
  brandBeige: "#F6EFE4",
  disabledGray: "#6C7A79",
  eventBgColor: "#ECF4F0",
  eventBgColorSecondary: "#F7EDD2",
};

export const commonTheme: ThemeOptions = {
  palette: {
    brand: {
      pink: colors.brandPink,
      blue: colors.brandBlue,
      green: colors.brandGreen,
      beige: colors.brandBeige,
    },
    common: {
      white: colors.white,
      black: colors.black,
    },
    primary: {
      main: colors.primary,
      light: colors.primaryLight,
      "500": colors.additionalGreen,
      "700": colors.accentGreen,
    },
    secondary: {
      main: colors.secondary,
    },
    text: {
      primary: colors.text,
      secondary: colors.textSecondary,
    },
    background: {
      default: colors.background,
      paper: colors.backgroundVariant,
      tertiary: colors.backgroundTertiary,
      quaternary: colors.backgroundQuaternary,
    },
    additionalGreen: {
      main: colors.additionalGreen,
      light: colors.greenLight,
    },
    yellow: {
      main: colors.yellow,
    },
    ambassadorPurple: {
      main: colors.ambassadorPurple,
    },
    disabledGray: {
      main: colors.disabledGray,
    },
    disabledGreen: {
      main: colors.green.light,
    },
    error: {
      main: colors.red[100],
      light: colors.red.light,
      dark: colors.red[150],
    },
    beige: {
      100: colors.beige[100],
    },
    delete: {
      main: colors.delete,
    },
    border: {
      main: colors.border,
    },
    eventBgColor: {
      main: colors.eventBgColor,
    },
    eventBgColorSecondary: {
      main: colors.eventBgColorSecondary,
    },
    pink: {
      main: colors.pink.main,
      dark: colors.pink.dark,
    },
  },
  zIndex: {
    tooltip: 99999,
  },

  typography: {
    fontFamily: "StackSansText",
    fontSize: 14,
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,
    htmlFontSize: 20,
    allVariants: {
      color: colors.text,
      fontFamily: "StackSansText",
    },
    // Headline Styles - Stack Sans Text
    h1: {
      fontWeight: 700,
      fontSize: "54px",
      lineHeight: 1.14,
      letterSpacing: "-0.02em",
    },
    // H1 with Source Serif 4 Medium Italic
    h1Serif: {
      fontFamily: "SourceSerif4",
      fontWeight: 500,
      fontStyle: "italic",
      fontSize: "60px",
      lineHeight: 1.2,
      letterSpacing: "-0.02em",
    },
    // H2 medium headline - Stack Sans Text
    h2: {
      fontWeight: 700,
      fontSize: "36px",
      lineHeight: 1.1,
      letterSpacing: "-0.02em",
    },
    // H2 with Source Serif 4 Medium Italic
    h2Serif: {
      fontFamily: "SourceSerif4",
      fontWeight: 500,
      fontStyle: "italic",
      fontSize: "44px",
      lineHeight: 1.16,
      letterSpacing: "-0.02em",
    },
    h3: {
      fontWeight: 700,
      fontSize: "28px",
      lineHeight: "1.3 !important",
    },
    h3Serif: {
      fontFamily: "SourceSerif4",
      fontWeight: 500,
      fontStyle: "italic",
      fontSize: "34px",
      lineHeight: "1.3 !important",
      letterSpacing: "-0.02em",
    },
    h4Serif: {
      fontFamily: "SourceSerif4",
      fontWeight: 500,
      fontStyle: "italic",
      fontSize: "14px",
      lineHeight: "20px !important",
      letterSpacing: "0",
    },
    // Subtitle 1 - Stack Sans Text Regular
    subtitle1: {
      fontWeight: 400,
      fontSize: "25px",
      lineHeight: 1.2,
      letterSpacing: "-0.02em",
    },
    // Subtitle 1 - Source Serif 4 Medium Italic
    subtitle1Serif: {
      fontFamily: "SourceSerif4",
      fontWeight: 500,
      fontStyle: "italic",
      fontSize: "30px",
      lineHeight: 1.17,
      letterSpacing: "-0.02em",
    },
    // Subtitle 2 - Stack Sans Text Regular
    subtitle2: {
      fontWeight: 400,
      fontSize: "18px",
      lineHeight: 1.28,
      letterSpacing: "-0.01em",
    },
    // Subtitle 2 - Source Serif 4 Medium Italic
    subtitle2Serif: {
      fontFamily: "SourceSerif4",
      fontWeight: 500,
      fontStyle: "italic",
      fontSize: "23px",
      lineHeight: 1.22,
      letterSpacing: "-0.02em",
    },
    // Body text - Stack Sans Text Bold
    body1: {
      fontWeight: 300,
      fontSize: "13px",
      lineHeight: "20px",
    },
    // Body text - Stack Sans Text Regular
    body2: {
      fontWeight: 400,
      fontSize: "15px",
      lineHeight: 1.33,
      letterSpacing: "-0.01em",
    },
    button: {
      fontSize: "13px",
      fontWeight: 700,
      lineHeight: "20px",
      letterSpacing: "-0.01em",
    },
    // Small text - Stack Sans Text Light
    caption: {
      fontWeight: 300,
      fontSize: "13px",
      lineHeight: 1.54,
      letterSpacing: "-0.01em",
    },
    // Small text - Stack Sans Text Bold
    smallBold: {
      fontWeight: 700,
      fontSize: "13px",
      lineHeight: 1.54,
      letterSpacing: "-0.01em",
    },
    text52Serif: {
      fontFamily: "SourceSerif4",
      fontWeight: 500,
      fontStyle: "italic",
      fontSize: "52px",
      lineHeight: "56px",
      letterSpacing: "-2px",
    },
    text50B: {
      fontSize: "50px",
      fontWeight: 700,
      lineHeight: "52px",
      letterSpacing: "-2px",
    },
    text44B: {
      fontSize: "44px",
      lineHeight: "56px",
      fontWeight: 700,
    },
    text36B: {
      fontSize: "36px",
      lineHeight: "44px",
      fontWeight: 700,
    },
    text28B: {
      fontSize: "28px",
      lineHeight: "32px",
      fontWeight: 700,
    },
    text28SB: {
      fontSize: "28px",
      lineHeight: "32px",
      fontWeight: 600,
    },
    text28M: {
      fontSize: "28px",
      lineHeight: "32px",
      fontWeight: 500,
    },
    text28R: {
      fontSize: "28px",
      lineHeight: "32px",
      fontWeight: 400,
    },
    // Text variants
    text24B: {
      fontSize: "24px",
      lineHeight: "20px",
      fontWeight: 700,
    },
    text24SB: {
      fontSize: "24px",
      fontWeight: 600,
      lineHeight: "40px",
    },
    text24M: {
      fontSize: "24px",
      lineHeight: "32px",
      fontWeight: 500,
    },
    text24R: {
      fontSize: "24px",
      lineHeight: "32px",
      fontWeight: 400,
    },
    text20B: {
      fontSize: "20px",
      lineHeight: "28px",
      fontWeight: 700,
    },
    text20SB: {
      fontSize: "20px",
      lineHeight: "1.4 !important",
      fontWeight: 600,
    },
    text20M: {
      fontSize: "20px",
      lineHeight: "1.4 !important",
      fontWeight: 500,
    },
    text20R: {
      fontSize: "20px",
      lineHeight: "1.4 !important",
      fontWeight: 400,
    },
    text18B: {
      fontSize: "18px",
      lineHeight: "28px",
      fontWeight: 700,
    },
    text18L: {
      fontSize: "18px",
      lineHeight: "28px !important",
      fontWeight: 300,
    },
    text16B: {
      fontSize: "16px",
      lineHeight: "24px",
      fontWeight: 700,
    },
    text16M: {
      fontSize: "16px",
      lineHeight: "24px",
      fontWeight: 500,
    },
    text16R: {
      fontSize: "16px",
      lineHeight: "24px",
      fontWeight: 400,
    },
    text15B: {
      fontSize: "15px",
      lineHeight: "24px",
      fontWeight: 700,
    },
    text15L: {
      fontSize: "15px",
      lineHeight: "24px",
      fontWeight: 300,
    },
    text14Serif: {
      fontFamily: "SourceSerif4",
      fontWeight: 500,
      fontStyle: "italic",
      fontSize: "14px",
      lineHeight: "20px",
    },
    text14B: {
      fontSize: "14px",
      lineHeight: "20px",
      fontWeight: 700,
    },
    text14M: {
      fontSize: "14px",
      lineHeight: "20px",
      fontWeight: 500,
    },
    text14R: {
      fontSize: "14px",
      lineHeight: "20px",
      fontWeight: 400,
    },
    text13B: {
      fontSize: "13px",
      lineHeight: "20px",
      fontWeight: 700,
    },
    text13M: {
      fontSize: "13px",
      lineHeight: "20px",
      fontWeight: 500,
    },
    text13R: {
      fontSize: "13px",
      lineHeight: "20px",
      fontWeight: 400,
    },
    text13L: {
      fontSize: "13px",
      lineHeight: "20px",
      fontWeight: 300,
    },
    text12B: {
      fontSize: "12px",
      lineHeight: "20px",
      fontWeight: 700,
    },
    text12M: {
      fontSize: "12px",
      lineHeight: "20px",
      fontWeight: 500,
    },
    text12L: {
      fontSize: "12px",
      lineHeight: "20px",
      fontWeight: 300,
    },
    text11B: {
      fontSize: "11px",
      lineHeight: "16px",
      fontWeight: 700,
    },
    text11M: {
      fontSize: "11px",
      lineHeight: "16px",
      fontWeight: 500,
    },
    text11L: {
      fontSize: "11px",
      lineHeight: "16px",
      fontWeight: 300,
    },
    // Additional variants
    numbers44B: {
      fontSize: "44px",
      lineHeight: "52px",
      fontWeight: 700,
    },
    cta48: {
      fontSize: "48px",
      lineHeight: "56px",
      fontWeight: 700,
    },
  },
};

const theme = createTheme({
  ...commonTheme,
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        "html, body": {
          scrollbarColor: `${colors.secondary} ${colors.background}`,
        },
        "*::-webkit-scrollbar": {
          width: 10,
          height: 10,
        },
        "*::-webkit-scrollbar-track": {
          backgroundColor: colors.background,
        },
        "*::-webkit-scrollbar-thumb": {
          backgroundColor: colors.secondary,
          borderRadius: 999,
          border: `2px solid ${colors.background}`,
        },
      },
    },
    MuiCard: {
      variants: [
        {
          props: { variant: "elevation" },
          style: {
            boxShadow: "box-shadow: 0 2px 15px 0 rgba(243, 239, 230, 0.40)",
          },
        },
        {
          props: { variant: "outlined" },
          style: {
            borderWidth: 1,
            borderColor: colors.secondary,
          },
        },
      ],
      styleOverrides: {
        root: {
          backgroundColor: colors.background,
          borderRadius: 20,
          padding: 24,
          "@media (max-width: 600px)": {
            padding: 16,
          },
          "& .MuiCardActions-root": {
            padding: 0,
            paddingTop: 16,
            gap: 1,
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          "&.MuiInputBase-root": {

          }
          // "& .MuiInputBase-root": {
          //   borderRadius: "6px !important",
          //   fontSize: "16px",
          //   fontWeight: 500,
          //   paddingRight: "18px !important",
          //   // maxHeight: "40px !important",
          //   backgroundColor: colors.backgroundVariant,
          //   padding: "8px 12px !important",
          //   "&:hover": {
          //     backgroundColor: colors.backgroundVariant,
          //   },
          //   "&.Mui-focused": {
          //     backgroundColor: colors.backgroundVariant,
          //   },
          // },
          // "& .MuiOutlinedInput-notchedOutline": {
          //   border: "none",
          // },
          // "& .MuiInputBase-input": {
          //   fontSize: "16px",
          //   fontWeight: 500,
          //   color: colors.text,
          //   paddingLeft: 8,
          //   paddingTop: "0 !important",
          //   paddingBottom: "0 !important",
          //   "&::placeholder": {
          //     color: colors.text,
          //     opacity: 1,
          //   },
          // },
          // "& .Mui-focused .MuiInputBase-input::placeholder": {
          //   color: colors.textSecondary,
          //   opacity: 1,
          // },
          // "& .MuiInputAdornment-root": {
          //   display: "flex",
          //   alignItems: "center",
          //   marginLeft: 8,
          //   marginRight: 0,
          //   color: "inherit",
          // },
        },
        paper: {
          paddingRight: 8,
          paddingTop: 16,
          paddingBottom: 24,
          paddingLeft: 16,
          marginTop: 8,
          borderRadius: 8,
          minWidth: "150px !important",
          backgroundColor: "#FFF",
          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
        },
        listbox: {
          paddingTop: 0,
          paddingBottom: 0,
          paddingRight: 8,
          maxHeight: "400px",
          "&::-webkit-scrollbar": {
            width: "4px",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "transparent",
            margin: "8px 0",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#bdbdbd",
            borderRadius: "3px",
            "&:hover": {
              backgroundColor: "#9e9e9e",
            },
          },
        },
        option: {
          paddingTop: "0 !important",
          paddingBottom: "0 !important",
          paddingLeft: 12,
          paddingRight: 12,
          minHeight: "32px !important",
          borderRadius: 6,
          marginTop: 8,
          marginBottom: 8,
          marginRight: 0,

          fontSize: "16px",
          fontWeight: 700,
          lineHeight: "24px",
          backgroundColor: `${colors.backgroundVariant} !important`,
          color: colors.text,
          "&[aria-selected='true']": {
            backgroundColor: `${colors.brandGreen} !important`,
            color: colors.textInverse
          },
          "&[data-focus='true']": {
            backgroundColor: `${colors.backgroundVariant} !important`,
          },
          "&[aria-selected='true'][data-focus='true']": {
            backgroundColor: `${colors.brandGreen} !important`,
            color: colors.textInverse
          },
          "&.Mui-selected": {
            backgroundColor: `${colors.brandGreen} !important`,
            color: colors.textInverse
          },
          "&.Mui-focusVisible": {
            backgroundColor: `${colors.backgroundVariant} !important`,
          },
          "&.Mui-selected.Mui-focusVisible": {
            backgroundColor: `${colors.brandGreen} !important`,
            color: colors.textInverse
          }
        },
        groupLabel: {
          fontSize: "16px",
          fontWeight: 700,
          color: colors.text,
          paddingLeft: 12,
          paddingTop: 16,
          marginBottom: 8,
        },
        popupIndicator: {
          color: "inherit",
          padding: 0,
          "&:hover": {
            backgroundColor: "transparent"
          },
          "&.MuiAutocomplete-popupIndicator": {
            "&:hover": {
              backgroundColor: "transparent"
            }
          }
        }
      },
    },
    MuiTextField: {
      variants: [
        {
          props: {
            color: "secondary"
          },
          style: {
            "& .MuiOutlinedInput-root": {

            },
          },
        },
      ],
      styleOverrides: {
        root: {
          "&.MuiInputBase-root": {
            fontWeight: 300,
            fontSize: "16px",
          },
          "& .MuiInputBase-input": {
            borderRadius: "6px !important",
            boxSizing: "border-box",
            padding: 0,
            maxHeight: 20,
            fontWeight: 300,
            fontSize: "16px",
            "&::placeholder": {
              fontSize: "13px",
              opacity: 1,
              color: "rgba(28, 72, 46, .6)",
            },
            '@media (max-width:600px)': {
              maxHeight: "44px",
            },
          },
          "& .MuiOutlinedInput-root": {
            padding: "8px 12px !important",
            borderRadius: "6px !important",
            "&::before": {
              display: "none",
            },
            "&::after": {
              display: "none",
            },
            "& .MuiInputBase-input": {
              padding: 0,
            },

            "&:hover fieldset": {
              border: `1px solid ${colors.brandGreen} !important`,
            },
            "&.Mui-focused fieldset": {
              border: `2px solid ${colors.primary} !important`,
            },
            "&.Mui-error": {
              backgroundColor: colors.backgroundVariant2,
              color: colors.red.main,
              "& fieldset": {
                border: `2px solid ${colors.red.dark} !important`,
              },
            },
          },
          "& .MuiFilledInput-root": {
            border: "none",
            padding: "8px 12px !important",
            borderRadius: "6px !important",
            boxSizing: "border-box",
            backgroundColor: "#FFF !important",
            // "&:hover": {
            //   backgroundColor: colors.backgroundVariant,
            // },
            // "&.Mui-focused": {
            //   backgroundColor: colors.backgroundVariant,
            // },
            // "&::placeholder": {
            //   color: "rgba(28, 72, 46, .6)",
            // },
            "& fieldset": {
              border: "none",
            },
            "&::before": {
              display: "none",
            },
            "&::after": {
              display: "none",
            },
          },
          "& .MuiInputAdornment-root": {
            "@media (max-width: 900px)": {
              marginRight: 4,
            },
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          margin: 0,
          marginTop: 4,
          color: colors.red.main,
          fontSize: "12px",
          fontWeight: 300,
          fontFamily: "StackSansText",
        },
      },
    },
    MuiSwipeableDrawer: {
      defaultProps: {
        PaperProps: {
          sx: {
            borderRadius: "16px 16px 0 0",
            boxShadow: "0 2px 15px 0 rgba(0, 0, 0, 0.40);",
            height: "95vh",
            backgroundColor: "background.default",
            width: "100%",
            maxWidth: "100%",
            mx: "auto",
            overflowX: "hidden",
            overflowY: "auto",
          },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          width: 28,
          height: 16,
          padding: 0,
          overflow: "visible",
          "& .MuiSwitch-switchBase": {
            padding: 0,
            margin: "2px",
            transitionDuration: "300ms",
            "&.Mui-checked": {
              transform: "translateX(12px)",
              color: "#fff",
              "& + .MuiSwitch-track": {
                backgroundColor: colors.secondary,
                opacity: 1,
                border: 0,
              },
            },
          },
          "& .MuiSwitch-thumb": {
            boxSizing: "border-box",
            width: 12,
            height: 12,
            color: "#fff",
          },
          "& .MuiSwitch-track": {
            borderRadius: 28 / 2,
            backgroundColor: colors.secondary,
            opacity: 0.75,
          },
        },
      },
      variants: [
        {
          props: { color: "secondary" },
          style: {
            "& .MuiSwitch-track": {
              backgroundColor: colors.additionalGreen,
            },
            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
              backgroundColor: colors.additionalGreen,
              opacity: 1,
              border: 0,
            },
          },
        },
      ],
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          textTransform: "none",
          boxShadow: "none",
          "&.MuiButton-outlined.MuiButton-sizeSmall": {
            padding: "7px 19px",
          },
          '@media (max-width:600px)': {
            minHeight: "44px",
          },
        },
        contained: {
          backgroundColor: "#000",
        },
        outlinedPrimary: {
          color: colors.primary,
          borderColor: colors.primary,
          backgroundColor: "transparent",
          "&.Mui-disabled": {
            borderColor: colors.disabled,
            color: colors.disabled,
          },
          small: {
            py: 1,
            px: 3.5,
            fontSize: "14px",
          }
        },
        outlinedError: {
          color: colors.red[100],
          borderColor: colors.red[100],
          backgroundColor: "transparent",
          "&:hover": {
            backgroundColor: colors.red[100],
            color: colors.textInverse,
          },
          "&.Mui-disabled": {
            borderColor: colors.disabled,
            color: colors.disabled,
          },
        },
        containedPrimary: {
          backgroundColor: colors.primary,
          color: colors.textInverse,
          "&.Mui-disabled": {
            backgroundColor: colors.grey.main,
            color: colors.grey.dark,
          },
          "&.Mui-Button-loadingIndicator": {
            color: colors.grey.dark,
          },
          "&.Mui-disabled.MuiButton-loading": {
            color: "transparent"
          },
        },
        containedError: {
          backgroundColor: colors.red[100],
          color: colors.textInverse,
          "&:hover": {
            backgroundColor: colors.red[150],
          },
          "&.Mui-disabled": {
            backgroundColor: colors.grey.main,
            color: colors.grey.dark,
          },
        },
        text: {
          color: colors.text,
          backgroundColor: "transparent",
          "&:hover": {
            backgroundColor: "transparent",
            color: colors.textSecondary,
          },
          // Disable animation on click (ripple and focus)
          "&.MuiButtonBase-root": {
            transition: "none !important",
            "&:active": {
              transition: "none !important",
            },
            "& .MuiTouchRipple-root": {
              display: "none !important",
            },
          },
        },
        textPrimary: {
          color: colors.primary,
          backgroundColor: "transparent",
          "&:hover": {
            backgroundColor: "transparent",
            color: colors.primary,
          },
        },
        textSecondary: {
          color: colors.secondary,
          backgroundColor: "transparent",
          "&:hover": {
            backgroundColor: "transparent",
            color: colors.secondary,
          },
        },
        sizeSmall: {
          padding: "8px 20px",
          fontSize: "14px",
          lineHeight: "18px",
        },
        sizeMedium: {
          height: 40,
          padding: "0px 20px",
          fontSize: "14px",
          lineHeight: "18px",
        },
        sizeLarge: {
          height: 52,
          padding: "0px 40px",
          fontSize: "1.1rem",
        },
        outlinedSecondary: {
          color: colors.text,
          backgroundColor: "transparent",
          borderColor: colors.secondary,
        },
        startIcon: {
          marginRight: "4px",
          marginLeft: 0,
        },
        endIcon: {
          marginLeft: "4px",
          marginRight: 0,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: colors.background,
          paddingTop: 12,
          paddingBottom: 12,
          paddingLeft: 28,
          paddingRight: 28,
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          p: 0,
          borderRadius: 12,
          boxShadow: "0 18px 40px 0 rgba(0, 0, 0, 0.16)",
          backgroundColor: colors.backgroundVariant,
        },
      },
    },
    MuiDialog: {
      defaultProps: {
        slotProps: {
          backdrop: {
            sx: {
              backgroundColor: "rgba(0, 0, 0, 0.6)",
            },
          },
        },
      },
      styleOverrides: {
        paper: {
          position: "relative",
          overflow: "visible",
          borderRadius: 16,
          padding: 0,
          backgroundColor: colors.background,
          boxShadow: "0px 18px 40px 0px rgba(0, 0, 0, 0.16)",

          "& .MuiDialogActions-root": {
            padding: "10px 24px",
          },
          "& .MuiDialogContent-root": {
            padding: "20px 24px",
          },
        },
      },
    },
    MuiDivider: {
      variants: [
        {
          props: { variant: "dashedWithLabel" },
          style: {
            border: "none",
            width: "100%",
            height: "1px",
            backgroundImage: "repeating-linear-gradient(to right, currentColor 0, currentColor 4px, transparent 4px, transparent 8px)",
            backgroundSize: "8px 1px",
            backgroundRepeat: "repeat-x",
            "&::before": {
              display: "none",
            },
            "&::after": {
              display: "none",
            },
            // When there's a wrapper, switch to flex layout
            "&:has(.MuiDivider-wrapper)": {
              display: "flex",
              alignItems: "center",
              height: "auto",
              backgroundImage: "none",
              "&::after": {
                display: "block",
                content: '""',
                flex: "1 1 auto",
                minWidth: 0,
                height: "1px",
                backgroundImage: "repeating-linear-gradient(to right, currentColor 0, currentColor 4px, transparent 4px, transparent 8px)",
                backgroundSize: "8px 1px",
                backgroundRepeat: "repeat-x",
              },
            },
            "& .MuiDivider-wrapper": {
              paddingRight: 8,
              paddingLeft: 0,
              flexShrink: 0,
            },
          },
        },
      ],
      styleOverrides: {
        root: {
          borderColor: colors.border,
          color: colors.textSecondary,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        label: {
          padding: "0 10px",
        },
        root: {
          borderRadius: 5,
          fontSize: "15px",
          fontWeight: 300,
          padding: "4px 0",
          lineHeight: "24px",
          backgroundColor: colors.backgroundVariant,
          gap: 4,
          height: "auto",
        },
        outlined: {
          borderWidth: 1,
          borderColor: "#E2E4E4",
          backgroundColor: "transparent",
        },
        deleteIcon: {
          margin: 0,
          color: colors.text,
        },
        icon: {
          margin: 0,
          color: colors.text,
        },
        sizeSmall: {
          height: "25px",
          maxHeight: "25px",
          fontSize: "11px",
          lineHeight: "16px",
          fontWeight: 300,
          "& .MuiChip-label": {
            padding: "0 8px",
          },
        },
        sizeMedium: {
          height: "28px",
          maxHeight: "28px",
          fontSize: "15px",
          lineHeight: "24px",
          fontWeight: 300,
        },
      },
      variants: [
        {
          props: { variant: "roundedOutlined" },
          style: {
            borderRadius: 9999,
            borderWidth: 1,
            borderStyle: "solid",
            borderColor: colors.brandGreen,
            backgroundColor: "transparent",
            padding: "4px 0",
            "& .MuiChip-label": {
              padding: "0 12px",
            },
          },
        },
        {
          props: { variant: "roundedFilled" },
          style: {
            borderRadius: 9999,
            backgroundColor: colors.brandGreen,
            color: colors.textInverse,
            padding: "4px 0",
            "& .MuiChip-label": {
              padding: "0 12px",
            },
          },
        },
      ],
    },
    MuiBreadcrumbs: {
      styleOverrides: {
        li: {
          color: colors.text,
          fontWeight: 700,
          fontSize: "14px",
          fontFamily: "StackSansText",
          "& a": {
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: 4,
          },
          "& p": {
            color: colors.textSecondary,
            fontWeight: 700,
            fontSize: "14px",
            fontFamily: "StackSansText",
          },
        },
        separator: {
          marginLeft: 4,
          marginRight: 4,
        }
      },
    },
    MuiSelect: {
      variants: [
        {
          props: { variant: "standard" },
          style: {
            minWidth: 0,
            flexShrink: 1,
            border: "none !important",
            backgroundColor: "transparent",
            "& .MuiSelect-select": {
              color: colors.primary,
              fontSize: "13px",
              fontWeight: 700,
              paddingLeft: 0,
              paddingTop: 0,
              paddingBottom: 0,
            },
            "&::before, &::after": {
              display: "none",
            },

            "&:hover": {
              backgroundColor: "transparent",
            },
            "&.Mui-focused": {
              backgroundColor: "transparent",
            },
            "&.Mui-error": {
              "& .MuiSelect-select": {
                color: colors.red[150],
                backgroundColor: colors.backgroundVariant2,
              },
            },
          },

        },
      ],
      styleOverrides: {
        root: {
          fontWeight: 300,
          fontSize: "13px",
          borderRadius: "6px !important",
          height: "36px",
          "&.Mui-error": {
            backgroundColor: colors.backgroundVariant2,
            "& .MuiSelect-select": {
              color: colors.red[150],
            },

            "& fieldset": {
              border: `2px solid ${colors.red[150]} !important`,
            },
            "&:hover fieldset": {
              border: `2px solid ${colors.red[150]} !important`,
            },
            "&.Mui-focused fieldset": {
              border: `2px solid ${colors.red[150]} !important`,
            },
          },
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          width: 44,
          height: 44,
          fontSize: 16,
          fontWeight: 700,
          color: colors.text,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          "&.Mui-selected": {
            backgroundColor: colors.primaryLight,
            "&:hover": {
              backgroundColor: colors.primaryLight,
            },
            "&.Mui-focusVisible": {
              backgroundColor: colors.primaryLight,
            },
          },
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: colors.background,
          boxSizing: 'border-box',
          paddingLeft: 12,
          paddingRight: 12,
          paddingTop: 20,
          paddingBottom: 20,
          border: "none",
          "& .MuiList-root": {
            paddingTop: 24,
            minHeight: 0,
            "& .Mui-selected": {
              backgroundColor: colors.beige[100],
              borderRadius: 6,
            },
          },
          "& .MuiListSubheader-root": {
            fontSize: "13px",
            fontWeight: 300,
            color: colors.textSecondary,
            backgroundColor: "transparent",
            padding: 8,
            lineHeight: "20px",
          },
          "& .MuiListItem-root": {
            padding: 8,
            gap: 8,
          },
          "& .MuiListItemIcon-root": {
            color: colors.brandGreen,
            minWidth: 0,
          },
          "& .MuiListItemText-root": {
            margin: 0,
          },
        },
      },
    },
    MuiPagination: {
      styleOverrides: {
        root: {
          border: `1px solid ${colors.secondary}`,
          borderRadius: 6,
          alignSelf: "center",
          marginTop: 12,
          overflow: "hidden",
        },
        ul: {
          display: "flex",
          "& li": {
            borderRight: `1px solid ${colors.secondary}`,
            "&:last-child": {
              borderRight: "none",
            },
          },
          "& .MuiPaginationItem-root": {
            fontWeight: 300,
            fontSize: "15px",
            lineHeight: "24px",
            margin: 0,
            borderRadius: 0,
            "&.Mui-selected": {
              backgroundColor: colors.secondary,
              color: colors.textInverse,
              "&:hover": {
                backgroundColor: colors.secondary,
              },
            },
            "&.Mui-disabled": {
              color: colors.disabled,
            },
            "&:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.04)",
            },
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: "32px",
          height: "32px",
          "& .MuiTabs-flexContainer": {
            gap: 32,
            height: "32px",
            borderBottom: `1px solid ${colors.border}`,
          },
          "& .MuiTabs-indicator": {
            height: "2px",
            backgroundColor: "text.primary",
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 300,
          fontSize: "13px",
          color: "text.secondary",
          padding: 0,
          minHeight: "32px",
          height: "32px",
          alignItems: "flex-start",
          width: "auto",
          flex: "0 1 auto",
          "&.Mui-selected": {
            color: `${colors.text}`,
          },
          "& .MuiTab-iconWrapper": {
            marginRight: 2,
          },
          '@media (min-width:600px)': {
            minWidth: 0,
          },
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          padding: 16,
          borderRadius: 12,
          backgroundColor: colors.white,
          pointerEvents: 'auto',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: colors.backgroundVariant,
          color: colors.text,
          fontSize: "11px",
          fontWeight: 700,
          padding: "6px 10px",
          borderRadius: 6,
          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.08)",
          textTransform: "none",
          letterSpacing: 0,
        },
        arrow: {
          color: colors.backgroundVariant,
        },
      },
    },
    MuiBadge: {
      styleOverrides: {
        badge: {
          backgroundColor: colors.red[150],
          color: colors.white,
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          WebkitFontSmoothing: "antialiased",
          MozOsxFontSmoothing: "grayscale",
          textRendering: "optimizeLegibility",
          color: colors.text, // Make text color default on Typography
        },
      },
      defaultProps: {
        variantMapping: {
          h1Serif: "h1",
          h2Serif: "h2",
          h3Serif: "h3",
          h4Serif: "h4",
          subtitle1Serif: "p",
          subtitle2Serif: "p",
          smallBold: "span",
          cta48: "p",
          numbers44B: "p",
          text52Serif: "p",
          text50B: "p",
          text44B: "p",
          text28B: "p",
          text28SB: "p",
          text28M: "p",
          text28R: "p",
          text24B: "p",
          text24M: "p",
          text24R: "p",
          text20B: "p",
          text20SB: "p",
          text20M: "p",
          text20R: "p",
          text18L: "p",
          text16B: "p",
          text16M: "p",
          text16R: "p",
          text15L: "p",
          text15B: "p",
          text14Serif: "p",
          text14B: "p",
          text14M: "p",
          text14R: "p",
          text13B: "p",
          text13M: "p",
          text13R: "p",
          text12B: "p",
          text12M: "p",
        },
      },
    },
    MuiSnackbar: {
      styleOverrides: {
        root: {
          "&.MuiSnackbar-anchorOriginBottomLeft": {
            bottom: "24px",
            left: "24px",
          },
          "&.MuiSnackbar-anchorOriginBottomCenter": {
            bottom: "24px",
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          padding: "12px 16px",
          fontSize: "14px",
          fontWeight: 500,
          boxShadow: "0 8px 32px 0 rgba(0, 0, 0, 0.08)",
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
          border: "1px solid",
          alignItems: "center",
          "& .MuiAlert-icon": {
            fontSize: "20px",
            padding: 0,
            marginRight: "12px",
            display: "flex",
            alignItems: "center",
          },
          "& .MuiAlert-message": {
            padding: 0,
            lineHeight: 1.4,
          },
          "& .MuiAlert-action": {
            padding: "0 0 0 12px",
            marginRight: "-4px",
          },
        },
        standardInfo: {
          backgroundColor: "rgba(235, 94, 42, 0.08)", // primary with opacity
          color: colors.primary,
          borderColor: "rgba(235, 94, 42, 0.2)",
          "& .MuiAlert-icon": {
            color: colors.primary,
          },
        },
        standardSuccess: {
          backgroundColor: "rgba(17, 112, 70, 0.08)", // additionalGreen with opacity
          color: colors.additionalGreen,
          borderColor: "rgba(17, 112, 70, 0.2)",
          "& .MuiAlert-icon": {
            color: colors.additionalGreen,
          },
        },
        standardWarning: {
          backgroundColor: "rgba(250, 204, 21, 0.08)", // yellow with opacity
          color: "#A16207", // Darker yellow for text contrast
          borderColor: "rgba(250, 204, 21, 0.3)",
          "& .MuiAlert-icon": {
            color: colors.yellow,
          },
        },
        standardError: {
          backgroundColor: "rgba(196, 18, 48, 0.08)", // red with opacity
          color: colors.red.main,
          borderColor: "rgba(196, 18, 48, 0.2)",
          "& .MuiAlert-icon": {
            color: colors.red.main,
          },
        },
        filledInfo: {
          backgroundColor: colors.primary,
          color: "#fff",
        },
        filledSuccess: {
          backgroundColor: colors.additionalGreen,
          color: "#fff",
        },
        filledWarning: {
          backgroundColor: colors.yellow,
          color: "rgba(0,0,0,0.8)",
        },
        filledError: {
          backgroundColor: colors.red.main,
          color: "#fff",
        },
      },
    },
  },
});

declare module "@mui/material/styles" {
  interface TypeBackground {
    tertiary?: string;
    quaternary?: string;
  }
  interface PaletteOptions {
    additionalGreen?: PaletteOptions['primary'];
    yellow?: PaletteOptions['primary'];
    ambassadorPurple?: PaletteOptions['primary'];
    disabledGray?: PaletteOptions['primary'];
    disabledGreen?: PaletteOptions['primary'];
    delete?: PaletteOptions['primary'];
    border?: PaletteOptions['primary'];
    eventBgColor?: PaletteOptions['primary'];
    eventBgColorSecondary?: PaletteOptions['primary'];
    brand: {
      pink: string;
      blue: string;
      green: string;
      beige: string;
    };
    beige: {
      100: string;
    };
    pink: {
      main: string;
      dark: string;
    };
  }
  interface Palette {
    additionalGreen: Palette['primary'];
    yellow: Palette['primary'];
    ambassadorPurple: Palette['primary'];
    disabledGray: PaletteOptions['primary'];
    delete: PaletteOptions['primary'];
    border: Palette['primary'];
    eventBgColor: Palette['primary'];
    eventBgColorSecondary: Palette['primary'];
    disabledGreen: Palette['primary'];
    brand: {
      pink: string;
      blue: string;
      green: string;
      beige: string;
    };
    pink: {
      main: string;
      dark: string;
    };
    beige: {
      100: string;
    };
  }

  interface TypographyVariants {
    h1Serif: CSSProperties;
    h2Serif: CSSProperties;
    h3Serif: CSSProperties;
    h4Serif: CSSProperties;
    subtitle1Serif: CSSProperties;
    subtitle2Serif: CSSProperties;
    smallBold: CSSProperties;
    text52Serif?: CSSProperties;
    text50B: CSSProperties;
    text44B: CSSProperties;
    text36B: CSSProperties;
    text24B: CSSProperties;
    text24SB: CSSProperties;
    text24M: CSSProperties;
    text24R: CSSProperties;
    text20B: CSSProperties;
    text20SB: CSSProperties;
    text20M: CSSProperties;
    text20R: CSSProperties;
    text18B: CSSProperties;
    text18L: CSSProperties;
    text16B: CSSProperties;
    text16M: CSSProperties;
    text16R: CSSProperties;
    text15L?: CSSProperties;
    text14Serif: CSSProperties;
    text14B: CSSProperties;
    text14M: CSSProperties;
    text14R: CSSProperties;
    text13B: CSSProperties;
    text13M: CSSProperties;
    text13R: CSSProperties;
    text12B: CSSProperties;
    text12M: CSSProperties;
    text12L: CSSProperties;
    text13L?: CSSProperties;
    text11B?: CSSProperties;
    text11M?: CSSProperties;
    text11L?: CSSProperties;
    numbers44B: CSSProperties;
    cta48: CSSProperties;
  }

  interface TypographyVariantsOptions {
    h1Serif?: CSSProperties;
    h2Serif?: CSSProperties;
    h3Serif?: CSSProperties;
    h4Serif?: CSSProperties;
    subtitle1Serif?: CSSProperties;
    subtitle2Serif?: CSSProperties;
    smallBold?: CSSProperties;
    text52Serif?: CSSProperties;
    text50B?: CSSProperties;
    text44B?: CSSProperties;
    text36B?: CSSProperties;
    text28B?: CSSProperties;
    text28SB?: CSSProperties;
    text28M?: CSSProperties;
    text28R?: CSSProperties;
    text24B?: CSSProperties;
    text24SB?: CSSProperties;
    text24M?: CSSProperties;
    text24R?: CSSProperties;
    text20B?: CSSProperties;
    text20SB?: CSSProperties;
    text20M?: CSSProperties;
    text20R?: CSSProperties;
    text18B?: CSSProperties;
    text18L?: CSSProperties;
    text16B?: CSSProperties;
    text16M?: CSSProperties;
    text16R?: CSSProperties;
    text15L?: CSSProperties;
    text15B?: CSSProperties;
    text14Serif?: CSSProperties;
    text14B?: CSSProperties;
    text14M?: CSSProperties;
    text14R?: CSSProperties;
    text13B?: CSSProperties;
    text13M?: CSSProperties;
    text13R?: CSSProperties;
    text13L?: CSSProperties;
    text12B?: CSSProperties;
    text12M?: CSSProperties;
    text12L?: CSSProperties;
    text11B?: CSSProperties;
    text11M?: CSSProperties;
    text11L?: CSSProperties;
    numbers44B?: CSSProperties;
    cta48?: CSSProperties;
  }
}

declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    h1Serif: true;
    h2Serif: true;
    h3Serif: true;
    h4Serif: true;
    subtitle1Serif: true;
    subtitle2Serif: true;
    smallBold: true;
    text52Serif: true;
    text50B: true;
    text36B: true;
    text44B: true;
    text24B: true;
    text24SB: true;
    text24M: true;
    text24R: true;
    text20B: true;
    text28B: true;
    text28SB: true;
    text28M: true;
    text28R: true;
    text20R: true;
    text20SB: true;
    text20M: true;
    text18B: true;
    text18L: true;
    text16B: true;
    text16M: true;
    text16R: true;
    text15L: true;
    text15B: true;
    text14Serif: true;
    text14B: true;
    text14M: true;
    text14R: true;
    text13B: true;
    text13M: true;
    text13R: true;
    text13L: true;
    text12B: true;
    text12M: true;
    text12L: true;
    text11B: true;
    text11M: true;
    text11L: true;
    numbers44B: true;
    cta48: true;

  }
}

declare module "@mui/material/styles" {
  interface PaletteOptions {
    brand: {
      pink: string;
      blue: string;
      green: string;
      beige: string;
    };
  }
}
export default theme;
